<template>
  <div class="main-container" :class="{ 'is-embedded': embedded }">
    <div class="container-area-personale">
      <!-- HEADER -->
      <div v-if="!embedded" class="header">
        <h2 class="title">ANAGRAFICA CLIENTI</h2>
      </div>

      <div class="main-content">
        <!-- DASHBOARD ACTIONS -->
        <div class="skeleton-loader" v-if="isLoadingClienti">
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-text"></div>
          <div class="skeleton skeleton-button"></div>
        </div>
        <div v-else class="dashboard" ref="dashboardRef">
          <div class="dashboard-actions">
            <label class="client-search-field">
              <i class="fa-solid fa-magnifying-glass"></i>
              <input
                v-model.trim="searchTerm"
                type="search"
                placeholder="Cerca cliente..."
                aria-label="Cerca cliente"
              />
            </label>
            <button
              class="btn-action"
              :class="{ 'active-button': isEditing }"
              @click="isEditing = !isEditing"
            >
              <i class="fa-solid fa-pen-to-square toolicon"></i> Modifica
            </button>
            <button class="btn-action" @click="openAddModal">
              <i class="fa-solid fa-plus toolicon"></i> Aggiungi Cliente
            </button>
            <div class="download-menu">
              <button class="btn-action download-menu-trigger" type="button">
                <i class="fas fa-download"></i> Scarica
                <i class="fa-solid fa-chevron-down download-menu-chevron"></i>
              </button>
              <div class="download-menu-dropdown">
                <button
                  class="download-menu-item"
                  type="button"
                  @click="scaricaExcelClienti"
                  :disabled="clienti.length === 0"
                >
                  <i class="fas fa-file-excel"></i> File attuale
                </button>
                <button
                  class="download-menu-item"
                  type="button"
                  @click="scaricaModelloAnagrafica"
                >
                  <i class="fas fa-file-excel"></i> Modello vuoto
                </button>
              </div>
            </div>
            <button class="btn-action" @click="openUploadModal">
              <i class="fas fa-upload"></i> Carica Anagrafica
            </button>
            <button class="btn-action" @click="eliminaFileAnagrafica">
              <i class="fas fa-trash"></i> Elimina Anagrafica
            </button>
            <span class="dashboard-actions-separator" aria-hidden="true"></span>
            <div class="dashboard-actions-save-group">
              <button
                class="btn-action addRowButton savebutton"
                :class="{ unsaved: hasUnsavedChanges }"
                title="Salva modifiche"
                @click="saveChanges"
              >
                <i class="fa-solid fa-floppy-disk"></i> Salva
              </button>

              <button
                class="btn-action addRowButton cancelbutton"
                :class="{ unsaved: hasUnsavedChanges }"
                title="Annulla modifiche"
                @click="cancelChanges"
              >
                <i class="fa-solid fa-xmark"></i> Annulla
              </button>
            </div>
          </div>
        </div>
        <div class="ultimamodifica">
          <span v-if="noChangesMessage" class="nochanges-message">
            Nessuna modifica presente
          </span>
        </div>
        <!-- CLIENT TABLE -->
        <div v-if="!isLoadingClienti" class="clienti-table-wrap">
          <table class="clienti-table" :key="tableKey" ref="exceltableRef">
            <thead>
              <tr>
                <th></th>
                <th>Denominazione Cliente</th>
                <th>Codice Fiscale</th>
                <th>Codice Ditta</th>
                <th v-if="isEditing"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="clientiFiltrati.length === 0">
                <td colspan="5" class="no-data">
                  {{
                    clienti.length === 0
                      ? "Nessun cliente presente"
                      : "Nessun cliente corrisponde alla ricerca"
                  }}
                </td>
              </tr>
              <template
                v-for="(
                  { cliente: c, originalIndex: index }, visibleIndex
                ) in clientiFiltrati"
              >
                <tr
                  :key="`${c.id}-main`"
                  :class="{
                    'row-deleting': deletedRows[index],
                    'highlight-row':
                      !deletedRows[index] &&
                      (hoveredRow === index || hoveredRow === 'all'),
                  }"
                >
                  <td class="row-index-cell">
                    <button
                      type="button"
                      class="registry-toggle"
                      :class="{ 'is-open': expandedClients[c.id] }"
                      :aria-expanded="Boolean(expandedClients[c.id])"
                      :title="
                        expandedClients[c.id]
                          ? 'Chiudi dettagli'
                          : 'Mostra alias e CC2'
                      "
                      @click.stop="toggleRegistryDetails(c.id)"
                    >
                      <i class="fa-solid fa-chevron-right"></i>
                    </button>
                    <span>{{ visibleIndex + 1 }}</span>
                  </td>

                  <!-- Denominazione Cliente -->
                  <td
                    :contenteditable="isEditing"
                    @dblclick="toggleEditing"
                    @input="
                      updateCell(
                        index,
                        'titolo_cliente',
                        $event.target.textContent,
                        $event.currentTarget
                      )
                    "
                    :class="{ modified: modifiedRows[index] }"
                  >
                    {{ c.titolo_cliente || "" }}
                    <span
                      v-if="aliasCount(c) || (c.centri_costo || []).length"
                      class="registry-summary"
                      contenteditable="false"
                    >
                      {{ aliasCount(c) }} alias ·
                      {{ (c.centri_costo || []).length }} CC2
                    </span>
                  </td>

                  <!-- Codice Fiscale -->
                  <td
                    :contenteditable="isEditing"
                    @dblclick="toggleEditing"
                    @input="
                      updateCell(
                        index,
                        'codice_fiscale',
                        $event.target.textContent,
                        $event.currentTarget
                      )
                    "
                    :class="{
                      modified: modifiedRows[index],
                      'invalid-cell': hasCodiceFiscaleError(c, index),
                    }"
                  >
                    {{ c.codice_fiscale || "" }}
                  </td>

                  <!-- Codice Ditta -->
                  <td
                    :contenteditable="isEditing"
                    @dblclick="toggleEditing"
                    @input="
                      updateCell(
                        index,
                        'codice_ditta',
                        $event.target.textContent,
                        $event.currentTarget
                      )
                    "
                    :class="{ modified: modifiedRows[index] }"
                  >
                    {{ c.codice_ditta || "" }}
                  </td>
                  <td v-if="isEditing" class="delete-column">
                    <button
                      class="delete-button"
                      :class="{ active: activeDelete[index] }"
                      title="Elimina Cliente"
                      @click="deleteRow(index, c, $event)"
                      @mouseenter="hoveredRow = index"
                      @mouseleave="hoveredRow = null"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
                <tr
                  v-if="expandedClients[c.id]"
                  :key="`${c.id}-registry`"
                  class="registry-detail-row"
                >
                  <td :colspan="isEditing ? 5 : 4">
                    <div class="registry-detail-panel">
                      <section class="registry-section">
                        <div class="registry-section-heading">
                          <div>
                            <span class="registry-eyebrow">Cliente</span>
                            <h4>Denominazioni</h4>
                          </div>
                          <span class="registry-count">
                            {{ (c.denominazioni || []).length }} totali
                          </span>
                        </div>
                        <div class="denomination-list">
                          <div
                            v-for="denominazione in c.denominazioni || []"
                            :key="denominazione.id"
                            class="denomination-item"
                            :class="{
                              'is-primary': denominazione.is_principale,
                            }"
                          >
                            <div class="denomination-copy">
                              <strong>{{ denominazione.denominazione }}</strong>
                              <small>
                                Fonte: {{ sourceLabel(denominazione.fonte) }} ·
                                rilevata {{ denominazione.numero_rilevazioni }}
                                {{
                                  denominazione.numero_rilevazioni === 1
                                    ? "volta"
                                    : "volte"
                                }}
                              </small>
                            </div>
                            <span
                              v-if="denominazione.is_principale"
                              class="primary-badge"
                            >
                              <i class="fa-solid fa-star"></i> Principale
                            </span>
                            <button
                              v-else
                              type="button"
                              class="make-primary-button"
                              @click="
                                confirmPromoteDenomination(c, denominazione)
                              "
                            >
                              Imposta principale
                            </button>
                          </div>
                          <p
                            v-if="!(c.denominazioni || []).length"
                            class="registry-empty"
                          >
                            Nessuna denominazione storicizzata.
                          </p>
                        </div>
                      </section>

                      <section
                        class="registry-section registry-section--centers"
                      >
                        <div class="registry-section-heading">
                          <div>
                            <span class="registry-eyebrow"
                              >Unità operative</span
                            >
                            <h4>Centri di costo CC2</h4>
                          </div>
                          <span class="registry-count">
                            {{ (c.centri_costo || []).length }} centri
                          </span>
                        </div>
                        <div class="cost-center-grid">
                          <article
                            v-for="centro in c.centri_costo || []"
                            :key="centro.id"
                            class="cost-center-card"
                          >
                            <header>
                              <span class="cc2-code">{{
                                centro.codice_cc2
                              }}</span>
                              <small>Associato a {{ c.titolo_cliente }}</small>
                            </header>
                          </article>
                          <p
                            v-if="!(c.centri_costo || []).length"
                            class="registry-empty"
                          >
                            Nessun centro di costo associato.
                          </p>
                        </div>
                      </section>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <!-- ADD CLIENT MODAL -->
    <div v-if="showAddModal" class="modal-backdrop">
      <div class="modal-content add-client-modal">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-user-plus"></i>
            Aggiungi Nuovo Cliente
          </h3>
          <button class="close-btn" @click="closeAddModal" title="Chiudi">
            ✕
          </button>
        </div>

        <!-- Messaggio di errore generale -->
        <div v-if="addError" class="error-banner">
          <i class="fa-solid fa-circle-exclamation"></i>
          {{ addError }}
        </div>

        <form @submit.prevent="salvaCliente" class="modal-form">
          <div class="form-group">
            <label for="titolo_cliente"
              >Denominazione Cliente <span class="required">*</span></label
            >
            <input
              id="titolo_cliente"
              type="text"
              v-model="newCliente.titolo_cliente"
              placeholder="Es. Rossi Mario Srl"
              :class="{ 'input-error': errors.titolo_cliente }"
              autofocus
            />
            <small v-if="errors.titolo_cliente" class="field-error">
              {{ errors.titolo_cliente }}
            </small>
          </div>

          <div class="form-group">
            <label for="codice_fiscale"
              >Codice Fiscale <span class="required">*</span></label
            >
            <input
              id="codice_fiscale"
              type="text"
              v-model="newCliente.codice_fiscale"
              maxlength="16"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              @input="formatCodiceFiscaleNuovoCliente"
              @blur="validateNewCodiceFiscale"
              placeholder="RSSMRA80A01H501Z"
              :class="{ 'input-error': errors.codice_fiscale }"
            />
            <small v-if="errors.codice_fiscale" class="field-error">
              {{ errors.codice_fiscale }}
            </small>
          </div>

          <div class="form-group">
            <label for="codice_ditta"
              >Codice Ditta <span class="optional">(facoltativo)</span></label
            >
            <input
              id="codice_ditta"
              type="text"
              v-model="newCliente.codice_ditta"
              maxlength="8"
              inputmode="numeric"
              @input="formatCodiceDittaNuovoCliente"
              placeholder="DIT12345"
            />
          </div>

          <div class="modal-actions">
            <button
              type="button"
              class="btn-action cancel"
              @click="closeAddModal"
            >
              Annulla
            </button>
            <button type="submit" class="btn-action primary">
              <i class="fa-solid fa-floppy-disk"></i>
              Salva Cliente
            </button>
          </div>
        </form>
      </div>
    </div>
    <!-- UPLOAD MODAL -->
    <div v-if="showModal" class="modal-backdrop">
      <div class="modal-content upload-modal">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-users"></i>
            Carica Anagrafica Clienti
          </h3>
          <button class="close-btn" @click="closeModal" title="Chiudi">
            ✕
          </button>
        </div>

        <!-- Messaggio di errore generale -->
        <div v-if="uploadError" class="error-banner">
          <i class="fa-solid fa-circle-exclamation"></i>
          {{ uploadError }}
        </div>

        <form @submit.prevent="caricaFile" class="modal-form">
          <!-- File Input -->
          <div class="form-group">
            <label for="fileInput"
              >Seleziona file Excel o CSV <span class="required">*</span></label
            >
            <input
              id="fileInput"
              type="file"
              ref="fileInput"
              accept=".xlsx,.xls,.csv"
              @change="handleFileChange"
              :class="{ 'input-error': errors.file }"
            />
            <small v-if="errors.file" class="field-error">
              {{ errors.file }}
            </small>
          </div>

          <!-- Modalità Import -->
          <div class="form-group">
            <label>
              Come vuoi importare i dati?
              <span class="required">*</span>
            </label>

            <div class="radio-group">
              <label class="radio-label">
                <input type="radio" value="aggiungi" v-model="modo" />
                <span>Aggiungi ai clienti esistenti</span>
              </label>
              <label class="radio-label">
                <input type="radio" value="sostituisci" v-model="modo" />
                <span>Sostituisci tutti i clienti esistenti</span>
              </label>
            </div>

            <!-- MESSAGGIO ROSSO COME NEL MODAL AGGIUNGI CLIENTE -->
            <div
              v-if="errors.modo"
              class="error-banner"
              style="margin-top: 10px; padding: 10px 12px; font-size: 0.95rem"
            >
              <i class="fa-solid fa-circle-exclamation"></i>
              {{ errors.modo }}
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-action cancel" @click="closeModal">
              Annulla
            </button>
            <button type="submit" class="btn-action primary">
              <i class="fa-solid fa-cloud-upload-alt"></i>
              Carica Anagrafica
            </button>
          </div>
        </form>
      </div>
    </div>
    <!-- CONFIRM MODAL -->
    <LibraryDeleteModal
      :visible="confirmModalVisibile"
      :title="confirmModalTitle"
      subtitle=""
      :message="confirmModalMessaggio"
      :confirm-label="confirmModalConfirmLabel"
      cancel-label="Annulla"
      @confirm="resolveConfirmModal(true)"
      @cancel="resolveConfirmModal(false)"
    />
    <!-- Success Modal -->
    <SuccessMessage
      :message="successModalMessaggio"
      :isVisible="successModalVisibile"
      @close="successModalVisibile = false"
    />

    <!-- Error Modal -->
    <SimpleErrorModal
      :message="erroreModalMessaggio"
      :isVisible="erroreModalVisibile"
      @close="erroreModalVisibile = false"
    />
  </div>
</template>

<script>
import { validFiscalCode } from "@/utils/fiscalCode.js";
import axios from "axios";
import { API_BASE_URL } from "@/config.js";
import { downloadModello } from "@/utils/downloads.js";
import SuccessMessage from "../components/SuccessMessage.vue";
import SimpleErrorModal from "../components/SimpleErrorModal.vue";
import LibraryDeleteModal from "../components/LibraryDeleteModal.vue";
// import SalvaModifiche from "../components/SalvaModifiche.vue";

export default {
  name: "AnagraficaClienti",
  props: {
    embedded: { type: Boolean, default: false },
  },
  components: {
    SuccessMessage,
    SimpleErrorModal,
    LibraryDeleteModal,
    // SalvaModifiche,
  },

  data() {
    return {
      clienti: [],
      clientiOriginali: [],
      expandedClients: {},
      searchTerm: "",
      tableKey: 0,
      modifiedRows: {},
      invalidClientRows: {},
      hasUnsavedChanges: false,
      isLoadingClienti: false,
      showAddModal: false,
      newCliente: {
        titolo_cliente: "",
        codice_fiscale: "",
        codice_ditta: "",
      },
      isEditing: false,
      showModal: false,
      file: null,
      modo: null,
      deletedRows: {},
      activeDelete: {},
      hoveredRow: null,
      deletedClientIds: [],
      confirmModalVisibile: false,
      confirmModalTitle: "Elimina Anagrafica Clienti",
      confirmModalMessaggio: "",
      confirmModalConfirmLabel: "Elimina",
      confirmModalCallback: null,
      successModalVisibile: false,
      successModalMessaggio: "",
      erroreModalVisibile: false,
      erroreModalMessaggio: "",
      noChangesMessage: false,
      addError: null,
      errors: {
        titolo_cliente: "",
        codice_fiscale: "",
        file: "",
        modo: "",
      },
    };
  },

  computed: {
    clientiFiltrati() {
      const query = this.searchTerm.trim().toLocaleLowerCase("it");

      return this.clienti
        .map((cliente, originalIndex) => ({ cliente, originalIndex }))
        .filter(({ cliente }) => {
          if (!query) return true;
          return [
            cliente.titolo_cliente,
            cliente.codice_fiscale,
            cliente.codice_ditta,
            ...(cliente.denominazioni || []).map((item) => item.denominazione),
            ...(cliente.centri_costo || []).map((centro) => centro.codice_cc2),
          ]
            .filter(Boolean)
            .some((value) =>
              String(value).toLocaleLowerCase("it").includes(query)
            );
        });
    },
  },

  mounted() {
    this.fetchClienti();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  methods: {
    toggleRegistryDetails(clientId) {
      this.$set(
        this.expandedClients,
        clientId,
        !this.expandedClients[clientId]
      );
    },
    aliasCount(cliente) {
      return (cliente.denominazioni || []).filter((item) => !item.is_principale)
        .length;
    },
    sourceLabel(source) {
      const labels = {
        manuale: "manuale",
        import: "import",
        f24: "F24",
        prospetti: "Prospetti",
        bilancio: "Bilancio",
        migrazione: "dati esistenti",
      };
      return labels[source] || source || "non indicata";
    },
    confirmPromoteDenomination(cliente, denominazione) {
      if (this.hasUnsavedChanges) {
        this.erroreModalMessaggio =
          "Salva o annulla le modifiche alla tabella prima di cambiare la denominazione principale.";
        this.erroreModalVisibile = true;
        return;
      }

      this.confirmModalTitle = "Cambia denominazione principale";
      this.confirmModalConfirmLabel = "Imposta principale";
      this.confirmModalMessaggio = `Impostare "${denominazione.denominazione}" come denominazione principale di ${cliente.titolo_cliente}? Il nome attuale resterà disponibile come alias.`;
      this.confirmModalVisibile = true;
      this.confirmModalCallback = async (confirmed) => {
        this.confirmModalVisibile = false;
        this.confirmModalCallback = null;
        this.confirmModalConfirmLabel = "Elimina";
        if (!confirmed) return;
        await this.promoteDenomination(cliente, denominazione);
      };
    },
    async promoteDenomination(cliente, denominazione) {
      const path = `/clienti/${cliente.id}/denominazioni/${denominazione.id}/promuovi`;
      try {
        const response = await axios.post(`${API_BASE_URL}${path}`);
        if (!response.data.success) {
          throw new Error(response.data.error || "Aggiornamento non riuscito");
        }
        await this.fetchClienti();
        this.$set(this.expandedClients, cliente.id, true);
        this.successModalMessaggio = response.data.message;
        this.successModalVisibile = true;
      } catch (error) {
        this.erroreModalMessaggio =
          error.response?.data?.error ||
          error.message ||
          "Impossibile cambiare la denominazione principale.";
        this.erroreModalVisibile = true;
      }
    },
    resolveConfirmModal(confirmed) {
      if (typeof this.confirmModalCallback === "function") {
        this.confirmModalCallback(confirmed);
        return;
      }
      this.confirmModalVisibile = false;
    },

    async fetchClienti() {
      this.isLoadingClienti = true;
      try {
        const res = await axios.get(`${API_BASE_URL}/get_clienti_by_studio`);
        this.clienti = res.data.success ? res.data.clienti : [];
        this.clientiOriginali = JSON.parse(JSON.stringify(this.clienti));
      } catch (err) {
        console.error(err);
        this.clienti = [];
      } finally {
        this.isLoadingClienti = false;
      }
    },

    toggleEditing() {
      if (!this.isEditing) {
        this.isEditing = true;
      }
    },
    handleClickOutside(event) {
      if (
        !this.$refs.exceltableRef?.contains(event.target) &&
        !this.$refs.dashboardRef?.contains(event.target)
      ) {
        this.isEditing = false;
        console.log("isEditing resettato a false da handleClickOutside");
      }
    },

    updateCell(index, field, rawValue, targetCell = null) {
      // Trova la cella DOM reale
      const table = document.querySelector(".clienti-table");
      if (!table) return;

      // Attenzione: l'indice delle colonne è fisso qui
      // 0 = nr riga, 1 = denominazione, 2 = cf, 3 = codice ditta
      let colIndex;
      if (field === "titolo_cliente") colIndex = 1;
      else if (field === "codice_fiscale") colIndex = 2;
      else if (field === "codice_ditta") colIndex = 3;
      else return;

      const row = table.rows[index + 1]; // +1 perché c'è thead
      const cell = targetCell || row?.cells[colIndex];
      if (!cell) return;

      const value =
        field === "codice_ditta"
          ? rawValue.replace(/\D/g, "").slice(0, 8)
          : field === "codice_fiscale"
          ? this.normalizeCodiceFiscaleInput(rawValue)
          : rawValue.trim();

      // Salva posizione cursore **prima** di modificare qualsiasi cosa
      const sel = window.getSelection();
      let cursorPos = sel?.anchorOffset ?? 0;
      if (sel?.anchorNode !== cell.firstChild) {
        // semplificazione – se non è esattamente sul text node può sbagliare
        cursorPos = value.length; // fallback
      }

      // ───────────────────────────────────────────────
      //  IMPORTANTE: NON modificare this.clienti qui!
      //  Lo facciamo dopo $nextTick
      // ───────────────────────────────────────────────

      this.$nextTick(() => {
        // Aggiorna visualmente la cella (senza triggerare re-render reattivo)
        cell.innerText = value;

        // Ripristina cursore
        if (cell.firstChild) {
          const range = document.createRange();
          range.setStart(cell.firstChild, Math.min(cursorPos, value.length));
          range.collapse(true);

          const newSel = window.getSelection();
          newSel.removeAllRanges();
          newSel.addRange(range);
        }

        // ORA sì, aggiorna il dato reattivo
        this.clienti[index][field] = value;

        if (field === "codice_fiscale") {
          this.updateCodiceFiscaleRowError(index, value);
        }

        // Gestione modified
        const originale = this.clientiOriginali[index]?.[field] || "";
        if (String(originale) !== String(value)) {
          this.$set(this.modifiedRows, index, true);
        } else {
          this.$delete(this.modifiedRows, index);
        }

        this.hasUnsavedChanges =
          JSON.stringify(this.clienti) !==
          JSON.stringify(this.clientiOriginali);
      });
    },

    openAddModal() {
      this.showAddModal = true;
      this.newCliente = {
        titolo_cliente: "",
        codice_fiscale: "",
        codice_ditta: "",
      };
      this.errors.titolo_cliente = "";
      this.errors.codice_fiscale = "";
    },

    closeAddModal() {
      this.showAddModal = false;
      this.errors.codice_fiscale = "";
      this.errors.titolo_cliente = "";
    },

    formatCodiceDittaNuovoCliente() {
      this.newCliente.codice_ditta = (this.newCliente.codice_ditta || "")
        .replace(/\D/g, "")
        .slice(0, 8);
    },

    normalizeCodiceFiscaleInput(value) {
      return String(value || "")
        .replace(/[^0-9A-Za-z]/g, "")
        .toUpperCase()
        .slice(0, 16);
    },

    formatCodiceFiscaleNuovoCliente() {
      this.newCliente.codice_fiscale = this.normalizeCodiceFiscaleInput(
        this.newCliente.codice_fiscale
      );

      if (
        this.errors.codice_fiscale &&
        this.isValidCodiceFiscale(this.newCliente.codice_fiscale)
      ) {
        this.errors.codice_fiscale = "";
      }
    },

    validateNewCodiceFiscale() {
      const codiceFiscale = this.normalizeCodiceFiscaleInput(
        this.newCliente.codice_fiscale
      );
      this.newCliente.codice_fiscale = codiceFiscale;

      if (!codiceFiscale) {
        this.errors.codice_fiscale = "Il Codice Fiscale è obbligatorio";
        return false;
      }

      if (!this.isValidCodiceFiscale(codiceFiscale)) {
        this.errors.codice_fiscale = "Inserisci un Codice Fiscale valido";
        return false;
      }

      this.errors.codice_fiscale = "";
      return true;
    },

    isValidCodiceFiscaleAzienda(value) {
      const val = this.normalizeCodiceFiscaleInput(value);
      return /^\d{11}$/.test(val);
    },

    isValidCodiceFiscalePersona(value) {
      const val = this.normalizeCodiceFiscaleInput(value);
      if (val.length !== 16 || !/^[A-Z0-9]{16}$/.test(val)) return false;

      const dispari = {
        0: 1,
        1: 0,
        2: 5,
        3: 7,
        4: 9,
        5: 13,
        6: 15,
        7: 17,
        8: 19,
        9: 21,
        A: 1,
        B: 0,
        C: 5,
        D: 7,
        E: 9,
        F: 13,
        G: 15,
        H: 17,
        I: 19,
        J: 21,
        K: 2,
        L: 4,
        M: 18,
        N: 20,
        O: 11,
        P: 3,
        Q: 6,
        R: 8,
        S: 12,
        T: 14,
        U: 16,
        V: 10,
        W: 22,
        X: 25,
        Y: 24,
        Z: 23,
      };
      const pari = {
        0: 0,
        1: 1,
        2: 2,
        3: 3,
        4: 4,
        5: 5,
        6: 6,
        7: 7,
        8: 8,
        9: 9,
        A: 0,
        B: 1,
        C: 2,
        D: 3,
        E: 4,
        F: 5,
        G: 6,
        H: 7,
        I: 8,
        J: 9,
        K: 10,
        L: 11,
        M: 12,
        N: 13,
        O: 14,
        P: 15,
        Q: 16,
        R: 17,
        S: 18,
        T: 19,
        U: 20,
        V: 21,
        W: 22,
        X: 23,
        Y: 24,
        Z: 25,
      };

      let sum = 0;
      for (let i = 0; i < 15; i++) {
        sum += i % 2 === 0 ? dispari[val[i]] : pari[val[i]];
      }

      const expectedCheck = String.fromCharCode(65 + (sum % 26));
      return expectedCheck === val[15];
    },

    isValidCodiceFiscale(value) {
      const val = this.normalizeCodiceFiscaleInput(value);
      return validFiscalCode(val);
    },

    updateCodiceFiscaleRowError(index, value) {
      const codiceFiscale = this.normalizeCodiceFiscaleInput(value);
      if (!codiceFiscale || !this.isValidCodiceFiscale(codiceFiscale)) {
        this.$set(this.invalidClientRows, index, true);
      } else {
        this.$delete(this.invalidClientRows, index);
      }
    },

    hasCodiceFiscaleError(cliente, index) {
      const codiceFiscale = this.normalizeCodiceFiscaleInput(
        cliente?.codice_fiscale
      );
      if (this.invalidClientRows[index]) return true;
      return Boolean(
        codiceFiscale && !this.isValidCodiceFiscale(codiceFiscale)
      );
    },

    validateModifiedClienti() {
      for (const [indexText] of Object.entries(this.modifiedRows)) {
        const index = Number(indexText);
        const cliente = this.clienti[index];
        const codiceFiscale = this.normalizeCodiceFiscaleInput(
          cliente?.codice_fiscale
        );

        if (cliente) {
          cliente.codice_fiscale = codiceFiscale;
        }

        if (!codiceFiscale || !this.isValidCodiceFiscale(codiceFiscale)) {
          this.$set(this.invalidClientRows, index, true);
          this.erroreModalMessaggio =
            "Correggi i Codici Fiscali evidenziati prima di salvare.";
          this.erroreModalVisibile = true;
          this.isEditing = true;
          return false;
        }
      }

      return true;
    },

    async salvaCliente() {
      // Reset errori
      this.addError = null;
      this.errors.titolo_cliente = "";
      this.errors.codice_fiscale = "";
      this.newCliente.titolo_cliente = (
        this.newCliente.titolo_cliente || ""
      ).trim();
      this.newCliente.codice_fiscale = this.normalizeCodiceFiscaleInput(
        this.newCliente.codice_fiscale
      );

      let hasError = false;

      // Validazione Denominazione
      if (!this.newCliente.titolo_cliente?.trim()) {
        this.errors.titolo_cliente = "La Denominazione Cliente è obbligatoria";
        hasError = true;
      }

      // Validazione Codice Fiscale
      if (!this.newCliente.codice_fiscale?.trim()) {
        this.errors.codice_fiscale = "Il Codice Fiscale è obbligatorio";
        hasError = true;
      }

      if (
        this.newCliente.codice_fiscale?.trim() &&
        !this.isValidCodiceFiscale(this.newCliente.codice_fiscale)
      ) {
        this.errors.codice_fiscale = "Inserisci un Codice Fiscale valido";
        hasError = true;
      }

      if (hasError) {
        // Focalizza il primo campo con errore
        this.$nextTick(() => {
          if (this.errors.titolo_cliente) {
            document.getElementById("titolo_cliente")?.focus();
          } else if (this.errors.codice_fiscale) {
            document.getElementById("codice_fiscale")?.focus();
          }
        });
        return;
      }

      try {
        const res = await axios.post(
          `${API_BASE_URL}/aggiungi_cliente`,
          this.newCliente
        );

        if (res.data.success) {
          await this.fetchClienti();
          this.closeAddModal();
        } else {
          this.addError =
            res.data.error || "Errore durante il salvataggio del cliente";
        }
      } catch (err) {
        console.error(err);
        this.addError = "Errore di connessione. Riprova più tardi.";
      }
    },

    openUploadModal() {
      this.showModal = true;
      this.file = null;
      this.modo = null;
      this.uploadError = null;
      this.errors.file = "";
      this.errors.modo = "";
    },

    closeModal() {
      this.showModal = false;
      this.file = null;
      this.modo = null;
      this.uploadError = null;
      this.errors.file = "";
      this.errors.modo = "";
    },

    handleFileChange(e) {
      this.file = e.target.files[0];
      if (this.file) this.errors.file = ""; // reset errore quando seleziona file
    },

    async caricaFile() {
      this.uploadError = null;
      this.errors.file = "";
      this.errors.modo = "";

      if (!this.file) {
        this.errors.file = "Seleziona un file da caricare";
        return;
      }

      if (!this.modo) {
        this.errors.modo = "Seleziona una modalità di importazione";
        return;
      }

      // Se arriva qui → tutto ok
      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("modo", this.modo);

      try {
        const res = await axios.post(
          `${API_BASE_URL}/carica_anagrafica`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (res.data.success) {
          await this.fetchClienti();
          this.closeModal();

          this.successModalMessaggio =
            res.data.message || "Anagrafica caricata con successo!";
          this.successModalVisibile = true;
        } else {
          this.erroreModalMessaggio =
            res.data.error || "Errore durante il caricamento dell'anagrafica.";
          this.erroreModalVisibile = true;
        }
      } catch (err) {
        console.error(err);
        let msg = "Errore di connessione. Riprova più tardi.";

        if (err.response?.data?.error) msg = err.response.data.error;
        else if (err.response?.status === 400)
          msg = "File non valido o formato errato.";
        else if (err.response?.status === 500)
          msg = "Errore interno del server.";

        this.erroreModalMessaggio = msg;
        this.erroreModalVisibile = true;
      }
    },

    async scaricaExcelClienti() {
      if (this.clienti.length === 0) {
        this.erroreModalMessaggio = "Nessun cliente da scaricare.";
        this.erroreModalVisibile = true;
        return;
      }

      try {
        const res = await axios.post(
          `${API_BASE_URL}/genera_excel_clienti`,
          { dati: this.clienti },
          { responseType: "blob" }
        );

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = "Clienti_Studio.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error(err);
        this.erroreModalMessaggio =
          "Errore durante il download del file Excel.";
        this.erroreModalVisibile = true;
      }
    },

    async scaricaModelloAnagrafica() {
      try {
        await downloadModello(
          "anagrafica_clienti",
          "MODELLO - Anagrafica Clienti.xlsx"
        );
        this.successModalMessaggio = "Modello scaricato con successo!";
        this.successModalVisibile = true;
      } catch (err) {
        console.error("Errore download modello anagrafica:", err);
        this.erroreModalMessaggio =
          err.response?.data?.error ||
          "Errore durante il download del modello.";
        this.erroreModalVisibile = true;
      }
    },

    async eliminaFileAnagrafica(event) {
      this.confirmModalTitle = "Elimina Anagrafica Clienti";
      this.confirmModalMessaggio =
        "Vuoi eliminare tutta l'anagrafica clienti?\n\nGli utenti del tuo studio non avranno più accesso a questi dati. Questa operazione è irreversibile.";

      this.confirmModalVisibile = true;

      this.confirmModalCallback = async (confirmed) => {
        if (confirmed) {
          document.documentElement.style.cursor = "progress";
          document.body.style.cursor = "progress";
          if (event?.target) event.target.style.cursor = "progress";

          try {
            const res = await axios.delete(
              `${API_BASE_URL}/elimina_anagrafica`
            );

            if (res.data.success) {
              this.clienti = [];
              this.successModalMessaggio = "Anagrafica eliminata con successo!";
              this.successModalVisibile = true;
            } else {
              this.erroreModalMessaggio =
                res.data.error || "Errore durante l'eliminazione.";
              this.erroreModalVisibile = true;
            }
          } catch (err) {
            console.error(err);
            this.erroreModalMessaggio =
              "Errore di rete o server durante l'eliminazione.";
            this.erroreModalVisibile = true;
          } finally {
            document.documentElement.style.cursor = "default";
            document.body.style.cursor = "default";
            if (event?.target) event.target.style.cursor = "pointer";
          }
        }

        this.confirmModalVisibile = false;
        this.confirmModalMessaggio = "";
        this.confirmModalCallback = null;
      };
    },

    async deleteRow(index, cliente, event) {
      this.confirmModalTitle = "Elimina cliente";
      this.confirmModalMessaggio = `Eliminare il cliente "${
        cliente?.titolo_cliente || "selezionato"
      }"? La riga verra rimossa dalla tabella e la modifica sara salvata solo premendo Salva.`;
      this.confirmModalConfirmLabel = "Elimina";
      this.confirmModalVisibile = true;
      this.confirmModalCallback = (confirmed) => {
        this.confirmModalVisibile = false;
        this.confirmModalTitle = "Elimina Anagrafica Clienti";
        this.confirmModalMessaggio = "";
        this.confirmModalConfirmLabel = "Elimina";
        this.confirmModalCallback = null;
        if (confirmed) {
          this.performDeleteRow(index, cliente, event);
        }
      };
    },

    performDeleteRow(index, cliente, event) {
      // ❌ NIENTE CONFIRM

      document.documentElement.style.cursor = "progress";
      document.body.style.cursor = "progress";
      if (event?.target) event.target.style.cursor = "progress";

      // evidenzia visivamente la riga
      this.$set(this.deletedRows, index, true);
      this.$set(this.activeDelete, index, true);

      setTimeout(() => {
        // salva ID per backend
        this.deletedClientIds.push(cliente.id);

        // rimuovi dalla UI
        this.clienti.splice(index, 1);

        // riallinea modifiedRows
        const newModified = {};
        Object.keys(this.modifiedRows).forEach((key) => {
          const i = Number(key);
          if (i === index) return;
          const newIndex = i > index ? i - 1 : i;
          newModified[newIndex] = true;
        });
        this.modifiedRows = newModified;

        // cleanup UI state
        this.$delete(this.deletedRows, index);
        this.$delete(this.activeDelete, index);
        this.hoveredRow = null;

        this.tableKey++;
        this.hasUnsavedChanges = true;

        document.documentElement.style.cursor = "default";
        document.body.style.cursor = "default";
        if (event?.target) event.target.style.cursor = "pointer";
      }, 500);
    },
    async saveChanges() {
      if (!this.hasUnsavedChanges) {
        this.noChangesMessage = true;
        setTimeout(() => {
          this.noChangesMessage = false;
        }, 2000);
        return;
      }

      const clientiModificati = this.clienti.filter(
        (_, index) => this.modifiedRows[index]
      );

      if (!this.validateModifiedClienti()) {
        return;
      }

      try {
        const res = await axios.post(`${API_BASE_URL}/update_clienti`, {
          clienti: clientiModificati,
          deleted_ids: this.deletedClientIds,
        });

        if (res.data.success) {
          this.successModalMessaggio = "Modifiche salvate con successo!";
          this.successModalVisibile = true;

          await this.fetchClienti();
          this.modifiedRows = {};
          this.invalidClientRows = {};
          this.deletedClientIds = [];
          this.hasUnsavedChanges = false;
          this.isEditing = false;
        } else {
          this.erroreModalMessaggio =
            res.data.error || "Errore durante il salvataggio.";
          this.erroreModalVisibile = true;
        }
      } catch (err) {
        console.error(err);
        this.erroreModalMessaggio =
          "Errore durante il salvataggio delle modifiche.";
        this.erroreModalVisibile = true;
      }
    },

    cancelChanges() {
      if (!this.hasUnsavedChanges) {
        this.noChangesMessage = true;

        setTimeout(() => {
          this.noChangesMessage = false;
        }, 2000);

        return;
      }

      this.clienti = JSON.parse(JSON.stringify(this.clientiOriginali));
      this.modifiedRows = {};
      this.invalidClientRows = {};
      this.hasUnsavedChanges = false;
      this.tableKey++;
    },
  },
};
</script>

<style scoped>
/* CONTAINER & HEADER */
.main-container.is-embedded {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  margin: 0;
  overflow: hidden;
}
.is-embedded .container-area-personale {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  height: 100%;
  min-height: 0;
  margin-bottom: 0;
  overflow: hidden;
}
.is-embedded .main-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  width: 100%;
  height: 0;
  min-height: 0;
  overflow: hidden;
}
.is-embedded .dashboard {
  flex: 0 0 auto;
  width: 100%;
  margin: 0 0 0.85rem;
}
.is-embedded .ultimamodifica {
  flex: 0 0 auto;
}
.clienti-table-wrap {
  width: 100%;
  overflow-x: auto;
}
.is-embedded .clienti-table-wrap {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow: auto;
  border: 1px solid #dfe7ef;
  border-radius: 12px;
  background: #ffffff;
}
.container-area-personale {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 4rem;
}
.header {
  display: flex;
  justify-content: center;
  align-items: baseline;
  padding: 1rem 2rem;
  gap: 18px;
}

/* DASHBOARD ACTIONS */
.dashboard {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}
.dashboard-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.client-search-field {
  display: inline-flex;
  flex: 0 1 clamp(8.75rem, 18%, 15.5rem);
  width: clamp(8.75rem, 18%, 15.5rem);
  min-width: 8.75rem;
  max-width: 15.5rem;
  min-height: 2.25rem;
  align-items: center;
  gap: 0.65rem;
  padding: 0 0.8rem;
  box-sizing: border-box;
  border: 1px solid #d4dfeb;
  border-radius: 8px;
  background: #fff;
  color: #28486e;
}
.client-search-field input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #233b5d;
  font: inherit;
  font-size: 0.84rem;
}
.client-search-field input::placeholder {
  color: #7c8da4;
  opacity: 1;
}
.dashboard-actions-separator {
  align-self: stretch;
  width: 1px;
  min-height: 2rem;
  margin: 0 0.85rem;
  background: #d6dee8;
}
.dashboard-actions-save-group {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}
.btn-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.55rem 1.1rem;
  font-size: 0.94rem;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: transparent;
  color: #444;
  transition: all 0.2s ease;
}
.is-embedded .dashboard-actions-save-group,
.is-embedded .dashboard-actions > .download-menu,
.is-embedded .btn-action {
  flex: 0 0 auto;
}
.is-embedded .btn-action {
  white-space: nowrap;
}
.btn-action:hover {
  background: #f3f4f6;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.btn-action:active {
  transform: translateY(0);
  background: #e5e7eb;
}
.active-button {
  background-color: #e0e0e0 !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* TABLE */
.clienti-table {
  width: 100%;
  width: 73rem;
  border-collapse: separate;
  border-spacing: 0;
}
.is-embedded .clienti-table {
  width: 100%;
  min-width: 62rem;
}
.is-embedded .clienti-table th {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #f3f4f6;
  background-clip: padding-box;
  box-shadow: 0 -1px 0 #f3f4f6, 0 1px 0 #dfe5eb;
}
.clienti-table th,
.clienti-table td {
  border: 1px solid #e2e6ea;
  padding: 0.55rem 0.65rem;
}
.clienti-table th {
  text-align: center;
  height: 1.25rem;
  color: #13243f;
  font-weight: 700;
  background: #f3f4f6;
}
.clienti-table th:nth-child(3),
.clienti-table td:nth-child(3),
.clienti-table th:nth-child(4),
.clienti-table td:nth-child(4) {
  min-width: 12rem;
}
.clienti-table th:nth-child(1),
.clienti-table td:nth-child(1) {
  width: 3.25rem;
  min-width: 3.25rem;
  max-width: 3.25rem;
  box-sizing: border-box;
  padding-right: 0.25rem;
  padding-left: 0.25rem;
}
.clienti-table td:nth-child(1) {
  font-family: monospace;
  color: #777;
  user-select: none;
}
.clienti-table td:nth-child(2) {
  text-align: left;
  padding-left: 0.75rem;
}
.clienti-table td {
  text-align: center;
}
.row-index-cell {
  white-space: nowrap;
  vertical-align: middle;
}
.registry-toggle {
  display: inline-flex;
  width: 1.25rem;
  height: 1.55rem;
  align-items: center;
  justify-content: center;
  margin-right: 0.1rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #53708f;
  cursor: pointer;
}
.registry-toggle:hover {
  background: #e9f1f9;
}
.registry-toggle i {
  font-size: 0.82rem;
  transition: transform 0.18s ease;
}
.registry-toggle.is-open i {
  transform: rotate(90deg);
}
.registry-summary {
  display: block;
  margin-top: 0.18rem;
  color: #718299;
  font-size: 0.72rem;
  font-weight: 600;
  pointer-events: none;
}
.registry-detail-row > td {
  padding: 0 !important;
  background: #f3f7fb !important;
  text-align: left !important;
}
.registry-detail-panel {
  display: grid;
  grid-template-columns: minmax(18rem, 0.9fr) minmax(24rem, 1.4fr);
  gap: 1rem;
  padding: 1rem 1.1rem 1.15rem 2.6rem;
  border-top: 2px solid #d9e5f1;
}
.registry-section {
  min-width: 0;
  padding: 0.9rem;
  border: 1px solid #d8e2ec;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 3px 10px rgba(28, 59, 91, 0.05);
}
.registry-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.registry-section-heading h4 {
  margin: 0.1rem 0 0;
  color: #17314f;
  font-size: 0.98rem;
}
.registry-eyebrow {
  color: #7789a0;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.registry-count {
  padding: 0.22rem 0.5rem;
  border-radius: 999px;
  background: #edf3f9;
  color: #506985;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}
.denomination-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.denomination-item {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  padding: 0.58rem 0.65rem;
  border: 1px solid #e0e7ef;
  border-radius: 7px;
  background: #fafcfe;
}
.denomination-item.is-primary {
  border-color: #b9d9c3;
  background: #f2faf4;
}
.denomination-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.16rem;
}
.denomination-copy strong {
  overflow: hidden;
  color: #243a54;
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.denomination-copy small {
  color: #78879a;
  font-size: 0.67rem;
}
.primary-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #287442;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
}
.primary-badge--small {
  font-size: 0.64rem;
}
.make-primary-button {
  flex: 0 0 auto;
  padding: 0.35rem 0.55rem;
  border: 1px solid #b8c7d8;
  border-radius: 6px;
  background: #fff;
  color: #315a83;
  font-size: 0.68rem;
  font-weight: 750;
  cursor: pointer;
}
.make-primary-button:hover {
  border-color: #6e98c2;
  background: #edf5fd;
}
.make-primary-button--small {
  padding: 0.25rem 0.4rem;
  font-size: 0.62rem;
}
.cost-center-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 0.65rem;
}
.cost-center-card {
  min-width: 0;
  padding: 0.7rem;
  border: 1px solid #d7e1eb;
  border-radius: 8px;
  background: #fbfcfe;
}
.cost-center-card > header {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 0.55rem;
}
.cost-center-card > header strong {
  overflow: hidden;
  color: #243a54;
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cc2-code {
  flex: 0 0 auto;
  padding: 0.25rem 0.48rem;
  border-radius: 5px;
  background: #193d64;
  color: #fff;
  font-family: monospace;
  font-size: 0.72rem;
  font-weight: 800;
}
.denomination-list--compact .denomination-item {
  padding: 0.4rem 0.5rem;
  font-size: 0.73rem;
}
.registry-empty {
  margin: 0;
  padding: 0.7rem;
  color: #7b899b;
  font-size: 0.78rem;
  font-style: italic;
}
@media (max-width: 900px) {
  .registry-detail-panel {
    grid-template-columns: 1fr;
    padding-left: 1rem;
  }
}
.clienti-table tbody tr:nth-child(odd) td {
  background: #ffffff;
}
.clienti-table tbody tr:nth-child(even) td {
  background: #f8f9fa;
}
.no-data {
  text-align: center;
  color: #888;
}

/* SKELETON */
.skeleton-loader {
  display: flex;
  width: 60rem;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
}

.skeleton {
  background-color: #e0e0e0;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}

.skeleton::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  height: 100%;
  width: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

.skeleton-title {
  width: 70%;
  height: 24px;
}

.skeleton-text {
  width: 100%;
  height: 16px;
}

.skeleton-button {
  width: 30%;
  height: 36px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
.skeleton-cell {
  height: 1.5rem;
  background: #e0e0e0;
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  50% {
    opacity: 0.4;
  }
}

/* MODAL */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.modal-content {
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  width: 420px;
  max-width: 100rem;
  min-width: 1rem !important;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}
.modal-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
}
.modal-actions button {
  width: 20rem;
  text-align: center;
}
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  text-align: left;
}
.checkbox-group label {
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.1rem;
  cursor: pointer;
}
.checkbox-group input[type="radio"] {
  width: 1rem;
  height: 0.8rem;
  margin-top: 0;
}
.btn-action.primary {
  background: #4caf50;
  color: white;
}
.btn-action.cancel {
  background: #888;
  color: white;
}
.modal-content input {
  width: 95%;
  margin-top: 0.5rem;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 6px;
}
.modified {
  background-color: #fff3cd;
}

.invalid-cell {
  background-color: #fee2e2 !important;
  color: #991b1b;
}

.delete-button {
  font-size: 0.9rem;
  color: #6b7280;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  transition: background-color 0.3s;
}
.delete-button:hover,
.delete-button.active {
  transform: scale(1.1); /* Aggiungi un effetto leggero di ingrandimento */
  background-color: #ffeeba !important;
  border-radius: 4px;
}

tr.row-deleting {
  background-color: #f8d7da;
  transition: all 0.3s ease;
  opacity: 0.5;
}
.row-deleting td {
  background-color: #ffdddd !important;
  color: #000 !important;
}
.row-deleting {
  opacity: 0.5;
  transform: scale(0.98);
  transition: all 0.2s ease;
}
.delete-column {
  min-width: 1rem !important;
  width: 3rem;
  text-align: center !important;
}
.highlight-row {
  background-color: #ffeeba !important;
  transition: background-color 0.3s ease-in-out !important;
}
tr.highlight-row td {
  background-color: #ffeeba !important;
  transition: background-color 0.3s ease-in-out !important;
}
tr.highlight-row td.invalid-cell {
  background-color: #fee2e2 !important;
}
tr.highlight-row .delete-button {
  background-color: #ffeeba;
  color: black !important;
}
.savebutton.unsaved {
  cursor: pointer;
  color: rgb(33, 105, 81);
  transform: scale(1.06);
}
.addRowButton.unsaved {
  background-color: #fbf9dd;
  transition: background-color 0.3s ease;
}
.addRowButton.unsaved:hover {
  background-color: #f7f3c1;
}
.cancelbutton.unsaved {
  opacity: 1;
  cursor: pointer;
  color: rgb(180, 39, 29);
  transform: scale(1.06);
  /*color: #ffa500; o un altro colore che preferisci */
}
.savebuttondiv:not(.unsaved):not(:disabled):hover {
  background-color: inherit !important; /* Mantieni il colore di sfondo */
  cursor: default; /* Cursore non interattivo */
}
.savebuttondiv:not(.unsaved) span {
  color: #cbcaca;
}
.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #c82333;
}

/* Grigio “Annulla” */
.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}
.cancelbutton:not(.unsaved):not(:disabled):hover,
.savebutton:not(.unsaved):not(:disabled):hover {
  cursor: default; /* Cursore non interattivo */
}
.savebutton:not(.unsaved) {
  color: #cbcaca;
}
.cancelbutton:not(.unsaved) {
  color: #cbcaca;
}
.ultimamodifica {
  position: relative;
  display: flex;
  flex-direction: column; /* floppy sopra, testo sotto */
  align-items: center;
}

.nochanges-message {
  position: absolute;
  width: 15rem;
  right: 6.1rem;
  top: -6.6rem;
  font-size: 0.96rem;
  color: gray;
  font-style: italic;
  transition: opacity 0.3s;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
.add-client-modal {
  width: 460px;
  padding: 0;
  overflow: hidden;
  border-radius: 16px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #eee;
  background: #f8fafc;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.35rem;
  color: #1e2937;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #fee2e2;
  color: #ef4444;
}

.modal-form {
  padding: 1.5rem 2rem 2rem;
}

.form-group {
  margin-bottom: 1.25rem;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
}

.required {
  color: #ef4444;
}

.modal-content input {
  width: 90%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.modal-content input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.input-error {
  border-color: #ef4444 !important;
}

.field-error {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.error-banner {
  background: #fee2e2;
  color: #b91c1c;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  margin: 1.25rem 2rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  border-left: 4px solid #ef4444;
}

/* Miglioramento bottoni (opzionale ma consigliato) */
.modal-actions {
  margin-top: 1.75rem;
  gap: 1rem;
}

.btn-action.primary {
  background: #2563eb;
  padding: 0.75rem 2rem;
  font-weight: 600;
}

.btn-action.primary:hover {
  background: #1d4ed8;
}
.upload-modal {
  width: 505px;
  padding: 0;
  overflow: hidden;
  border-radius: 16px;
}

/* Radio buttons migliorati */
.radio-group {
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.97rem;
  cursor: pointer;
  padding: 0.3rem 0;
}

.radio-label input[type="radio"] {
  width: 12px;
  height: 12px;
  accent-color: #2563eb;
}

.modal-content input[type="file"] {
  width: 95%;
  padding: 0.6rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  cursor: pointer;
}

.modal-content input[type="file"]:hover {
  border-color: #94a3b8;
}

.modal-content input[type="file"].input-error {
  border-color: #ef4444;
}

/* Mantiene leggibile la toolbar sui notebook senza occupare spazio eccessivo. */
@media (min-width: 1101px) and (max-height: 940px) {
  .is-embedded .dashboard {
    margin-bottom: 0.76rem;
  }

  .is-embedded .dashboard-actions {
    gap: 0.3rem;
    padding: 0.4rem 0.55rem;
    border-radius: 11px;
  }

  .is-embedded .client-search-field {
    flex-basis: 12rem;
    width: 12rem;
    min-width: 9.5rem;
    max-width: 12rem;
    min-height: 2.03rem;
    gap: 0.5rem;
    padding-right: 0.65rem;
    padding-left: 0.65rem;
  }

  .is-embedded .client-search-field input {
    font-size: 0.76rem;
  }

  .is-embedded .btn-action {
    gap: 0.4rem;
    padding: 0.48rem 0.6rem;
    font-size: 0.83rem;
  }

  .is-embedded .dashboard-actions > .btn-action,
  .is-embedded .dashboard-actions > .download-menu {
    flex: 1 1 auto;
    min-width: 0;
  }

  .is-embedded .dashboard-actions > .download-menu > .btn-action {
    width: 100%;
  }

  .is-embedded .dashboard-actions-separator {
    min-height: 1.8rem;
    margin-right: 0.35rem;
    margin-left: 0.35rem;
  }

  .is-embedded .dashboard-actions-save-group {
    gap: 0.3rem;
    margin-left: 0;
  }

  .is-embedded .clienti-table {
    font-size: 0.9rem;
  }

  .clienti-table th,
  .clienti-table td {
    padding: 0.5rem 0.58rem;
  }

  .clienti-table th {
    height: 1.12rem;
  }

  .clienti-table td:nth-child(2) {
    padding-left: 0.68rem;
  }
}
</style>
